"use client";
import React, { useContext, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Context } from "../contexts/AppContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import { getData, postData } from "../../lib/utils";

function ReplaceVideo(props) {
  const [videoFiles, setVideoFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const xhrRefs = useRef({});
  const [totalFileUploaded, setTotalFileUploaded] = useState(0);

  const { value, progress } = useContext(Context);
  const [appState] = value;
  const {
    currentFolderData,
    currentGalleryData,
    currentFolderName,
    currentGalleryName,
  } = appState;

  const { showProgressBar, cancelledIdsRef, stopProgress } = progress;

const sanitizeFileName = (name) => name.replace(/[^a-zA-Z0-9_.]/g, "");



  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    setVideoFiles(files);
    props?.replaceStart(props?.videoId)
  };

  useEffect(() => {
    if (videoFiles.length > 0) handleUpload();
  }, [videoFiles]);

  // --- Backend API calls ---
  const startMultipartUpload = async (fileName, fileType) => {
    const s3Key = `${currentFolderData.adminId}/${currentFolderData.id}/${currentGalleryData._id}/${fileName}`;
    return await postData(
      `products/start-multipart-upload?fileName=${encodeURIComponent(
        s3Key
      )}&fileType=${fileType}`
    );
  };

  const getPartUrl = async (fileName, uploadId, partNumber) => {
    const s3Key = `${currentFolderData.adminId}/${currentFolderData.id}/${currentGalleryData._id}/${fileName}`;
    return await getData(
      `products/get-part-url?fileName=${encodeURIComponent(
        s3Key
      )}&uploadId=${uploadId}&partNumber=${partNumber}`
    );
  };

  const completeMultipartUpload = async (_fileName, uploadId, parts) => {
    const fileName = `${currentFolderData.adminId}/${currentFolderData.id}/${currentGalleryData._id}/${_fileName}`;
    return await postData(`products/complete-multipart-upload`, {
      fileName,
      uploadId,
      parts,
    });
  };

  // --- Multipart Upload Logic ---
  const uploadMultipartFile = async (file, uniqueId) => {
    const cleanName = sanitizeFileName(file.name);

    // Dynamic chunk size 50MB–200MB
    const chunkSize = Math.min(
      Math.max(file.size / 1000, 50 * 1024 * 1024),
      200 * 1024 * 1024
    );
    const totalParts = Math.ceil(file.size / chunkSize);

    showProgressBar?.(cleanName, 0, uniqueId, "uploading");

    try {
      const { uploadId } = await startMultipartUpload(cleanName, file.type);
      let parts = [];
      let uploadedBytes = 0;

      for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
        const start = (partNumber - 1) * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);

        const { url } = await getPartUrl(cleanName, uploadId, partNumber);

        const xhr = new XMLHttpRequest();
        xhrRefs.current[uniqueId] = xhr;

        await new Promise((resolve, reject) => {
          xhr.open("PUT", url, true);

          if (cancelledIdsRef?.has?.(uniqueId)) {
            xhr.abort();
            showProgressBar?.(cleanName, 0, uniqueId, "cancelled");
            return reject("Upload cancelled");
          }

          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable && !cancelledIdsRef?.has?.(uniqueId)) {
              uploadedBytes = start + event.loaded;
              const percent = Math.round((uploadedBytes / file.size) * 100);
              showProgressBar?.(cleanName, percent, uniqueId, "uploading");
              props?.showProgressBar?.(cleanName, percent);
            }
          };

          xhr.onload = () => {
            if (xhr.status === 200) {
              const eTag = xhr.getResponseHeader("ETag");
              parts.push({ ETag: eTag, PartNumber: partNumber });
              resolve();
            } else reject(`Part ${partNumber} failed`);
          };

          xhr.onerror = () => reject(`Network error on part ${partNumber}`);
          xhr.send(chunk);
        });
      }

      // --- Complete Upload ---
      await completeMultipartUpload(cleanName, uploadId, parts);

      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      setTotalFileUploaded((prev) => prev + 1);

      props?.replaceUploadVideoDone?.(
        cleanName,
        uniqueId,
        props.videoId,
        props.oldVideoUrl
      );

      showProgressBar?.(cleanName, 100, uniqueId, "completed");
      stopProgress(uniqueId);
    } catch (err) {
      console.error("Multipart upload error:", err);
      showProgressBar?.(
        cleanName,
        0,
        uniqueId,
        cancelledIdsRef?.has?.(uniqueId) ? "cancelled" : "failed"
      );
    } finally {
      delete xhrRefs.current[uniqueId];
    }
  };

  // --- Single File Replace Upload ---
  const handleUpload = async () => {
    setIsUploading(true);

    for (const file of videoFiles) {
      const uniqueId = uuidv4();
      try {
        await uploadMultipartFile(file, uniqueId);
      } catch (err) {
        console.error("Replace upload failed:", err);
      }
    }

    setIsUploading(false);
    setVideoFiles([]);
  };

  return (
    <div>
      <input
        type="file"
        accept="video/*"
        onChange={handleFileChange}
        id="fileReplace"
        style={{ display: "none" }}
      />

      <button
        onClick={() => document.getElementById("fileReplace").click()}
        className="flex items-center gap-x-2 w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
      >
        <FontAwesomeIcon icon={faPencil} style={{ color: "#854d0e" }} /> Replace
      </button>
    </div>
  );
}

export default ReplaceVideo;
